import React, { FC } from "react"
import { graphql } from "gatsby"
import { renderRichText } from "gatsby-source-contentful/rich-text"
import { useSwipeable } from "react-swipeable"

import { BentoBoxBlockFragmentFragment, BentoBoxCardFragmentFragment } from "@graphqlTypes"

import { generateConditionalClassName, processShortcodes } from "@utils"

import IconButton, { ICON_BUTTON_SIZES } from "@components/IconButton/IconButton"
import LpSection from "@components/lp/LpSection/LpSection"
import Media from "@components/lp/Media/Media"
import { ICON_TYPES } from "@helpscout/hs-marketing-shared-components"

import useCarousel from "./BentoBoxBlock.hooks"

import "./BentoBoxBlock.scss"
import AnchorLink from "@components/AnchorLink/AnchorLink"

export enum BENTO_BOX_VARIANTS {
  DEFAULT = "DEFAULT",
  CAROUSEL = "CAROUSEL",
  MASONRY = "MASONRY",
}

export enum BENTO_BOX_FEATURE_COLORS {
  COBALT = "COBALT",
  MAGENTA = "MAGENTA",
  PURPLE = "PURPLE",
  GREEN = "GREEN",
}

const WHITE_BG_CSS_REGEX = /(var\(--color--basics-white\)|white|#fff(fff)?|#FFF(FFF)?);?/

export const BentoBoxCard: FC<BentoBoxCardFragmentFragment & {
  cardBackgroundCss?: BentoBoxBlockFragmentFragment["cardBackgroundCss"],
  className?: string,
  onClick?: () => void,
  variant: BENTO_BOX_VARIANTS
}> = ({
  cardBackgroundCss,
  className,
  ctaText,
  media,
  onClick,
  text,
  title,
  url,
  variant,
  wistiaId,
}) => {
    const _children = (
      <>
        <div className="content">
          <div>
            <h3 className="type--headline-semibold-L">{title}</h3>
            {text && typeof text.raw === "string" && renderRichText({ raw: text.raw, references: [] })}
          </div>
          {variant === BENTO_BOX_VARIANTS.MASONRY && (
            <span className="button">{ctaText || "Learn more"}</span>
          )}
        </div>
        <div className="image">
          {(media || wistiaId) && (
            <Media
              excludeRoundedCorners
              image={media}
              playVideoInline
              wistiaId={wistiaId}
            />
          )}
        </div>
      </>
    )

    if (url) return (
      <AnchorLink
        className={`BentoBoxBlock--Card${className ? ` ${className}` : ""}`}
        href={url}
        onClick={onClick}
        style={{ background: cardBackgroundCss || undefined }}
      >
        {_children}
      </AnchorLink>
    )

    if (onClick) return (
      <button
        className={`BentoBoxBlock--Card${className ? ` ${className}` : ""}`}
        onClick={onClick}
        style={{ background: cardBackgroundCss || undefined }}
      >
        {_children}
      </button>
    )

    return (
      <div
        className={`BentoBoxBlock--Card${className ? ` ${className}` : ""}`}
        onClick={onClick}
        style={{ background: cardBackgroundCss || undefined }}
      >
        {_children}
      </div>
    )
  }

const BentoBoxBlock: FC<BentoBoxBlockFragmentFragment> = ({
  background,
  cardBackgroundCss,
  contentful_id,
  featureColor,
  includeBackground,
  items,
  marginBottom,
  text,
  title,
  useDarkMode,
  variant = BENTO_BOX_VARIANTS.DEFAULT,
}) => {
  const isCarousel = variant === BENTO_BOX_VARIANTS.CAROUSEL
  const {
    carouselOffset,
    isCarouselDisabledLeft,
    isCarouselDisabledRight,
    moveCarouselLeft,
    moveCarouselRight,
  } = useCarousel(isCarousel, items ? items.length : 0)

  const swipeHandlers = useSwipeable({
    onSwipedLeft: moveCarouselRight,
    onSwipedRight: moveCarouselLeft
  })

  return (
    <LpSection
      className={generateConditionalClassName({
        BentoBoxBlock: true,
        [`variant--${variant}`]: true,
        [`feature-color--${featureColor}`]: !!featureColor,
        [`items-count--${items?.length || 0}`]: true,
        "has-dark-mode-with-white-bg": !!useDarkMode && !!includeBackground && !!background?.match(WHITE_BG_CSS_REGEX)
      })}
      background={background}
      includeBackground={includeBackground}
      marginBottom={marginBottom}
      text={!isCarousel ? text : undefined}
      title={!isCarousel ? title : undefined}
      uniqueId={contentful_id}
      useDarkMode={useDarkMode}
    >
      <div className="container">
        {isCarousel && (
          <div className={`BentoBox--carousel-controls${text ? " has-text" : ""}`}>
            <div>
              {title && <h2 className="type--headline-medium-4XL margin-bottom--0">{title}</h2>}
              {text && (
                <div className="margin-top--XS">
                  {typeof text?.raw === "string" &&
                    renderRichText({ raw: text.raw, references: [] })}
                </div>
              )}
            </div>
            <div className="BentoBox--carousel-controls-buttons">
              <IconButton
                aria-label="Advance Carousel to the left"
                disabled={isCarouselDisabledLeft}
                iconType={ICON_TYPES["arrow-left"]}
                onClick={moveCarouselLeft}
                size={ICON_BUTTON_SIZES.M}
              />
              <IconButton
                aria-label="Advance Carousel to the right"
                disabled={isCarouselDisabledRight}
                iconType={ICON_TYPES["arrow-right"]}
                onClick={moveCarouselRight}
                size={ICON_BUTTON_SIZES.M}
              />
            </div>
          </div>
        )}
        <div
          className="BentoBoxBlock--Cards flex"
          style={{
            translate:
              isCarousel ?
                `calc(${carouselOffset} * (100% / var(--BentoBoxBlock--n-cards-up) - var(--BentoBoxBlock--cards-gap) * (var(--BentoBoxBlock--n-cards-up) - 1) / var(--BentoBoxBlock--n-cards-up) + var(--BentoBoxBlock--cards-gap)))` :
                undefined,
          }}
          {...swipeHandlers}
        >
          {items?.map(item => item ? (
            <BentoBoxCard
              {...item}
              cardBackgroundCss={cardBackgroundCss}
              variant={variant as BENTO_BOX_VARIANTS}
            />
          ) : null)}
        </div>
      </div>
    </LpSection>
  )
}

export const BentoBoxBlockFragment = graphql`
  fragment BentoBoxCardFragment on ContentfulLpBentoBoxItem {
    ctaText
    media {
      title
      imgixImage {
        gatsbyImageData(width: 1052)
      }
    }
    text {
      raw
    }
    title
    url
    wistiaId
  }

  fragment BentoBoxBlockFragment on ContentfulLpBentoBoxBlock {
    contentful_id
    featureColor
    items {
      ...BentoBoxCardFragment
    }
    text {
      raw
    }
    title
    variant
    background
    cardBackgroundCss
    includeBackground
    marginBottom
    useDarkMode
  }
`

export default BentoBoxBlock
